import type { DesktopSetupInput } from '../schemas/desktop'
import type { AiProxyCredentials } from '../ai'

/**
 * Pusula masaüstü — supervisor (Electron main), backend ve frontend arasında paylaşılan tipler.
 * Bu modül yalnızca tip ve sabit içerir; Node/Electron API'si kullanmaz.
 */

/** Veri Merkezinin varsayılan HTTPS portu */
export const DEFAULT_DATACENTER_PORT = 47100

/** Ağda keşif için mDNS servis tipi (`_pusula._tcp`) */
export const MDNS_SERVICE_TYPE = 'pusula'

export interface SmtpSettings {
  host: string
  port: number
  secure: boolean
  user: string
  password: string
  from: string
}

/**
 * Veri Merkezi ayarları — `<VeriKlasörü>/pusula-datacenter.json` içinde saklanır.
 * Veri klasörüyle birlikte taşınır (makine değişiminde ayarlar korunur).
 */
export interface DataCenterSettings {
  port: number
  /** Dinlenen arayüz: '0.0.0.0' (tüm ağlar) ya da belirli bir IP */
  bindAddress: string
  mdnsEnabled: boolean
  /** Kayıtlı cihaz olmadan tarayıcıdan erişime izin ver */
  browserAccess: boolean
  /** Yeni cihazlar admin onayı bekler */
  deviceApprovalRequired: boolean
  /** Boşsa tüm adresler; dolu ise yalnızca bu IP/CIDR'lar (loopback her zaman serbest) */
  ipAllowlist: string[]
  /** Günlük otomatik yedek saati ('HH:mm'); null = kapalı */
  backupTime: string | null
  /** Saklanacak otomatik yedek sayısı */
  backupRetention: number
  /** Yedek klasörü; null = <VeriKlasörü>/backups */
  backupDir: string | null
  updateChannel: 'stable' | 'beta'
  smtp: SmtpSettings | null
}

export const DEFAULT_DATACENTER_SETTINGS: DataCenterSettings = {
  port: DEFAULT_DATACENTER_PORT,
  bindAddress: '0.0.0.0',
  mdnsEnabled: true,
  browserAccess: false,
  deviceApprovalRequired: false,
  ipAllowlist: [],
  backupTime: '02:00',
  backupRetention: 14,
  backupDir: null,
  updateChannel: 'stable',
  smtp: null,
}

export interface DataCenterManifest {
  formatVersion: 1
  /** Veri Merkezinin kalıcı kimliği (mDNS, davet linki, lisans) */
  dcId: string
  companyName: string
  createdAt: string
  /** Bu veri klasörünü en son açan uygulama sürümü */
  appVersion: string
  settings: DataCenterSettings
}

// ─── Supervisor ↔ backend (utilityProcess parentPort) ──────────────────────────

/** Backend'in supervisor'dan isteyebileceği işlemler */
export interface SupervisorMethods {
  /** Ağ ayarı değişti → backend yeni ayarla yeniden başlatılır */
  restartNetwork: { params: Record<string, never>; result: { ok: true } }
  /** Yeniden başlatma gerektirmeyen ayarlar (yedek zamanlaması, mDNS) dosyadan yeniden okunur */
  reloadSettings: { params: Record<string, never>; result: { ok: true } }
  rotateCertificate: { params: Record<string, never>; result: { fingerprint: string } }
  status: { params: Record<string, never>; result: SupervisorStatus }
  /** Davet/bağlantı linki için güncel adresler, port ve parmak izi (token: null) */
  connectionInfo: { params: Record<string, never>; result: InviteLink }
  backupNow: { params: { kind: BackupKind }; result: BackupInfo }
  listBackups: { params: Record<string, never>; result: BackupInfo[] }
  deleteBackup: { params: { id: string }; result: { ok: true } }
  /** Kabul edilince supervisor backend'i durdurup geri yükler (yanıt beklenmez) */
  restoreBackup: { params: { id: string }; result: { accepted: true } }
  /** Seçilen yedek dosyasındaki kayıtları mevcut verinin ÜSTÜNE ekler (silmez); yol `pickBackupFile` ile onaylanmış olmalı */
  importData: { params: { path: string }; result: DataImportReport }
  /** Ana makine penceresinde klasör seçtirir; seçilirse taşıma başlar */
  moveDataDir: { params: Record<string, never>; result: { accepted: boolean } }
  deleteOldDataDir: { params: Record<string, never>; result: { ok: true } }
  firewallRepair: { params: Record<string, never>; result: { ok: boolean } }
  checkForUpdates: { params: Record<string, never>; result: UpdateState }
  licenseValidate: { params: Record<string, never>; result: LicenseState }
  /** Yapay Zekâ proxy'si için aktivasyon kimliği (lisans yoksa null) — sır yalnızca backend belleğinde tutulur */
  aiCredentials: { params: Record<string, never>; result: AiProxyCredentials | null }
}

export type SupervisorMethod = keyof SupervisorMethods

export type SupervisorRequest = {
  [M in SupervisorMethod]: { type: 'call'; id: number; method: M; params: SupervisorMethods[M]['params'] }
}[SupervisorMethod]

export type SupervisorResponse =
  | { type: 'result'; id: number; ok: true; data: unknown }
  | { type: 'result'; id: number; ok: false; error: string }

/** Supervisor → backend bildirimleri */
export type SupervisorEvent =
  | { type: 'shutdown' }
  | { type: 'license'; state: LicenseState }
  | { type: 'announce'; message: 'UPDATE_RESTART'; seconds: number }

export interface SupervisorStatus {
  hostname: string
  os: string
  addresses: string[]
  port: number
  appVersion: string
  pgVersion: string
  startedAt: string
  dataDir: string
  dataDirBytes: number
  filesBytes: number
  freeDiskBytes: number
  certificate: { fingerprint: string; validUntil: string } | null
  firewall: 'OK' | 'MISSING' | 'UNKNOWN'
  lastBackupAt: string | null
}

/** "Veri yükle (birleştir)" sonucu: tablo başına kaynak/eklenen/birleşen/atlanan/yeniden adlandırılan kayıt sayısı */
export interface DataImportTableReport { source: number; inserted: number; merged: number; skipped: number; renamed: number }
export interface DataImportReport {
  tables: Record<string, DataImportTableReport>
  usersAdded: number
  usersMatched: number
  totalInserted: number
  /** Yedekten kopyalanan belge/ek dosyası sayısı */
  filesCopied: number
}

export type BackupKind = 'AUTO' | 'MANUAL' | 'PRE_UPDATE' | 'PRE_RESTORE' | 'PRE_IMPORT'

export interface BackupInfo {
  id: string
  fileName: string
  kind: BackupKind
  sizeBytes: number
  createdAt: string
  appVersion: string
}

export type LicenseStatus = 'VALID' | 'GRACE' | 'READONLY' | 'NONE'

export interface LicenseState {
  status: LicenseStatus
  plan: string | null
  userLimit: number | null
  validUntil: string | null
  lastValidatedAt: string | null
  graceDaysLeft: number | null
  machineId: string
  licenseId: string | null
  message: string | null
}

export type UpdateState =
  | { status: 'idle'; currentVersion: string; lastCheckedAt: string | null }
  | { status: 'checking'; currentVersion: string }
  | { status: 'available'; currentVersion: string; info: UpdateInfo }
  | { status: 'downloading'; currentVersion: string; info: UpdateInfo; percent: number }
  | { status: 'downloaded'; currentVersion: string; info: UpdateInfo }
  | { status: 'not-available'; currentVersion: string; lastCheckedAt: string }
  | { status: 'error'; currentVersion: string; message: string }

export interface UpdateInfo {
  version: string
  releaseDate: string
  releaseNotes: string
  sizeBytes: number | null
  mandatory: boolean
  /** İstemcide: ana makine henüz bu sürüme geçmediyse false (önce ana makine kuralı) */
  allowed: boolean
}

/** Backend → supervisor mesajları */
export type BackendMessage = SupervisorRequest | { type: 'ready' }

// ─── Preload köprüsü (window.pusulaDesktop) ─────────────────────────────────────

export type AppMode = 'host' | 'client'

export interface DesktopAppInfo {
  mode: AppMode | null
  appVersion: string
  /** Bu pencere ana makinede mi açık (Veri Merkezi yerel işlemleri yalnızca burada) */
  isHost: boolean
  hostname: string
  dataDir: string | null
  /** Taşıma sonrası silinmeyi bekleyen eski veri klasörü */
  previousDataDir: string | null
}

export interface DataDirCheck {
  path: string
  ok: boolean
  /** Klasörde mevcut bir Veri Merkezi var */
  existing: { dcId: string; companyName: string; createdAt: string } | null
  freeBytes: number
  problems: Array<'NOT_WRITABLE' | 'LOW_SPACE' | 'SYNC_FOLDER' | 'NETWORK_DRIVE' | 'NOT_EMPTY' | 'NOT_NTFS'>
}

export interface HostSetupInput {
  dataDir: string
  port: number
  /** Yeni Veri Merkezinde firma ve yönetici; buluttan taşımada null (paketten gelir) */
  setup: DesktopSetupInput | null
  /** Lisansa bağlı eski bulut firmasının verisi taşınsın */
  migrate?: boolean
}

export type SetupStep =
  | 'DATA_DIR' | 'POSTGRES' | 'BACKEND' | 'TENANT' | 'CERTIFICATE' | 'FIREWALL' | 'IMPORT' | 'DONE'

export interface SetupProgress {
  step: SetupStep
  status: 'running' | 'done' | 'error'
  message?: string
  percent?: number
}

export interface InviteLink {
  dcId: string
  addresses: string[]
  port: number
  fingerprint: string
  /** Davet token'ı (kullanıcıya özel); genel bağlantı linkinde yoktur */
  token: string | null
  companyName: string
}

/** Ana süreçten pencereye gelen olaylar */
export type DesktopEvent =
  | { type: 'setup-progress'; progress: SetupProgress }
  | { type: 'update'; state: UpdateState; /** güncelleme penceresi açılsın mı */ notify: boolean }
  | { type: 'quit-requested' }
  | { type: 'connection'; status: 'connected' | 'reconnecting' | 'unreachable' }

export interface DesktopBridge {
  getInfo(): Promise<DesktopAppInfo>
  /**
   * Kurulum/bağlantı bitince Veri Merkezinin arayüzünü aç.
   * `login` verilirse ana süreç bu bilgilerle yerel Veri Merkezi'nde oturum açıp uygulamayı oturum açık yükler (kurulum sonrası).
   */
  openApp(login?: { email: string; password: string }): Promise<void>
  onEvent(listener: (e: DesktopEvent) => void): () => void

  // Lisans (Pusula Portal hesabı ya da lisans anahtarı)
  licenseLogin(email: string, password: string): Promise<{ authToken: string; user: { email: string; firstName: string; lastName: string }; licenses: PortalLicenseSummary[] }>
  activateLicense(target: LicenseActivationTarget): Promise<PortalLicenseSummary>

  // Kurulum (ana makine)
  pickDirectory(): Promise<string | null>
  checkDataDir(path: string): Promise<DataDirCheck>
  installHost(input: HostSetupInput): Promise<{ ok: true; url: string; report?: MigrationReport } | { ok: false; error: string; report?: MigrationReport }>
  openExistingHost(path: string): Promise<{ ok: true; url: string } | { ok: false; error: string }>
  /** Port çakışmasında hata ekranından yeni port seçimi; ardından yeniden başlatılır */
  changeHostPort(port: number): Promise<void>

  // İstemci
  parseInvite(link: string): Promise<InviteLink | null>
  pickConnectionFile(): Promise<InviteLink | null>
  /** Veri yükleme için `.pusulabak` dosyası seçtirir; seçilen yol yalnızca bu oturumda onaylı sayılır (yoksa null) */
  pickBackupFile(): Promise<string | null>
  discover(): Promise<InviteLink[]>
  /** Davet token'lı linkte onay ekranı bilgisi (Veri Merkezine ulaşılamazsa null) */
  previewInvite(invite: InviteLink): Promise<{ companyName: string; email: string | null; firstName: string } | null>
  connect(invite: InviteLink): Promise<{ ok: true; url: string } | { ok: false; error: string }>
  retryConnection(): Promise<void>
  resetMode(): Promise<void>

  // Ana makinede yerel işlemler
  openDataDir(): Promise<void>
  importMigrationPackage(): Promise<{ ok: boolean; error?: string }>
  saveFile(defaultName: string, content: string): Promise<boolean>

  // Güncelleme
  getUpdateState(): Promise<UpdateState>
  checkForUpdates(): Promise<UpdateState>
  downloadUpdate(): Promise<void>
  installUpdate(): Promise<void>
  remindLater(): Promise<void>
  skipVersion(version: string): Promise<void>

  setOpenAtLogin(enabled: boolean): Promise<void>
  quit(force: boolean): Promise<void>
  /** Ana makine: Veri Merkezi'ni kapat ve karşılama ekranına dön (başka veri klasörü açmak için) */
  switchDataCenter(): Promise<void>
}

// ─── Bağlantı / davet linkleri ──────────────────────────────────────────────────

/** Varsayılan Pusula Portal adresi (lisans API'si, davet yedek sayfası, indirmeler) */
export const DEFAULT_PORTAL_URL = 'https://app.pusulaerp.com.tr'

/** Sertifika parmak izi: SHA-256, küçük harf hex, ayraçsız (64 karakter) */
export function normalizeFingerprint(value: string): string {
  return value.replace(/^sha256\//i, '').replace(/[^0-9a-f]/gi, '').toLowerCase()
}

/**
 * `pusula://join?d=<dcId>&n=<firma>&a=<adres1,adres2>&p=<port>&f=<parmak izi>[&t=<davet token'ı>]`
 * Token'sız link "genel bağlantı linki"dir (mevcut kullanıcının yeni cihazı).
 */
export function buildJoinLink(info: InviteLink): string {
  return `pusula://join?${joinQuery(info)}`
}

/** E-posta için yedek: portal sayfası, bilgiler `#` sonrasında (sunucuya gönderilmez) */
export function buildJoinPageUrl(portalUrl: string, info: InviteLink): string {
  return `${portalUrl.replace(/\/$/, '')}/join#${joinQuery(info)}`
}

function joinQuery(info: InviteLink): string {
  const q = new URLSearchParams({
    d: info.dcId,
    n: info.companyName,
    a: info.addresses.join(','),
    p: String(info.port),
    f: normalizeFingerprint(info.fingerprint),
  })
  if (info.token) q.set('t', info.token)
  return q.toString()
}

/** `pusula://join?…`, portal `/join#…` linki ya da yalnızca sorgu kısmını çözer; geçersizse null */
export function parseJoinLink(input: string): InviteLink | null {
  const text = input.trim()
  const query = text.includes('#') ? text.slice(text.indexOf('#') + 1) : text.includes('?') ? text.slice(text.indexOf('?') + 1) : text
  const q = new URLSearchParams(query)
  const port = Number(q.get('p'))
  const fingerprint = normalizeFingerprint(q.get('f') ?? '')
  const addresses = (q.get('a') ?? '').split(',').map((a) => a.trim()).filter(Boolean)
  const dcId = q.get('d') ?? ''
  if (!dcId || addresses.length === 0 || !Number.isInteger(port) || port < 1 || port > 65535 || fingerprint.length !== 64) return null
  return { dcId, companyName: q.get('n') ?? '', addresses, port, fingerprint, token: q.get('t') }
}

// ─── Lisans ─────────────────────────────────────────────────────────────────────

/**
 * Lisans token'larını doğrulayan Ed25519 açık anahtarı (özel anahtar yalnızca portal sunucusunda,
 * `LICENSE_SIGNING_PRIVATE_KEY`). Anahtar değişirse tüm kurulu sürümler yeni sürüme geçmelidir.
 */
export const LICENSE_PUBLIC_KEY_PEM = `-----BEGIN PUBLIC KEY-----
MCowBQYDK2VwAyEAR2zzoRXeXMFf2+dJ/SHl7CimRGV+XCiA1yl+OXc2piU=
-----END PUBLIC KEY-----`

/** İmzalı lisans token'ının içeriği: `base64url(JSON).base64url(imza)` */
export interface LicenseTokenPayload {
  v: 1
  /** lisans kimliği */
  lid: string
  /** aktivasyon kimliği */
  aid: string
  /** makine özeti (SHA-256) */
  machine: string
  plan: string
  userLimit: number
  companyName: string
  validUntil: string
  graceDays: number
  /** imza zamanı (ISO) */
  iat: string
}

/** Portal lisans hesabındaki bir lisansın özeti (kurulumda seçim için) */
export interface PortalLicenseSummary {
  id: string
  keyPrefix: string
  companyName: string
  plan: string
  userLimit: number
  validUntil: string
  status: 'ACTIVE' | 'SUSPENDED' | 'REVOKED'
  activeActivations: number
  maxActivations: number
  taxOffice: string | null
  taxNumber: string | null
  /** Eski bulut firmasından dönüştürüldüyse buluttan veri taşınabilir */
  legacyTenantId: string | null
}

/** 14 günlük deneme başvurusu (kurulum sihirbazı): portal hesabı + deneme lisansı + aktivasyon tek adımda */
export interface TrialRequest {
  companyName: string
  firstName: string
  lastName: string
  email: string
}

/** Lisans etkinleştirme: portal hesabındaki lisans, lisans anahtarı ya da yeni deneme */
export type LicenseActivationTarget =
  | { licenseId: string; authToken: string }
  | { licenseKey: string }
  | { trial: TrialRequest }

export interface LicenseActivationResult {
  token: string
  activationId: string
  activationSecret: string
  license: PortalLicenseSummary
}

// ─── Buluttan veri taşıma ───────────────────────────────────

/** Tablo başına satır sayısı ve sayısal kolon toplamları (kaynak ve hedefte aynı hesaplanır) */
export interface MigrationStats {
  tables: Record<string, number>
  sums: Record<string, string>
}

/**
 * Taşıma paketi: AES-256-GCM ile şifreli zip (`[12 bayt IV][şifreli veri][16 bayt etiket]`).
 * Zip içeriği: manifest.json, db.dump (pg_dump -Fc, tek şema), tenant.json, users.json, files/<anahtar>
 */
export interface MigrationManifest {
  formatVersion: 1
  tenantId: string
  schemaName: string
  companyName: string
  /** Kaynak şemada uygulanmış son tenant migration'ı */
  sourceMigration: string | null
  createdAt: string
  dbSha256: string
  stats: MigrationStats
  files: Array<{ key: string; sha256: string; size: number }>
  /** Referansı olup depoda bulunamayan dosyalar */
  missingFiles: string[]
}

export type DataMigrationStatus = 'PENDING' | 'PREPARING' | 'READY' | 'DOWNLOADED' | 'COMPLETED' | 'FAILED' | 'EXPIRED'

export interface MigrationReport {
  ok: boolean
  migrationId: string
  companyName: string
  sourceMigration: string | null
  tables: number
  rows: number
  files: number
  missingFiles: string[]
  differences: Array<{ kind: 'table' | 'sum' | 'file'; key: string; source: string; target: string }>
  importedAt: string
}
